import { PaymentStatus } from '../Mollie/types';
import { CurrencyCode } from '../../helpers/CurrencyHelpers';

interface Model {
    id: number;
    created_at?: string;
    updated_at?: string;
    class?: string;
}

interface ModelWithSoftDelete extends Model {
    deleted_at?: string | null;
}

export const SKR_VALUES = ['skr03', 'skr04', 'skr42'] as const;
export type Skr = (typeof SKR_VALUES)[number];

export const TAX_CODES = [
    'standard',
    'reduced',
    'zero',
    'exempt_4',
    'exempt_4_input',
    'small_business_19',
    'reverse_charge_13b',
    'intra_community',
    'non_taxable',
    'out_of_scope'
] as const;
export type TaxCode = (typeof TAX_CODES)[number];

export const SPHERES = ['ideell', 'vermoegensverwaltung', 'zweckbetrieb', 'wirtschaftlich'] as const;
export type Sphere = (typeof SPHERES)[number];

export const LEGAL_FORMS = ['verein_gemeinnuetzig', 'verein', 'gmbh', 'ug', 'einzelunternehmen', 'sonstige'] as const;
export type LegalForm = (typeof LEGAL_FORMS)[number];

export type ProfitDetermination = 'bilanz' | 'euer';
export type TaxationMethod = 'soll' | 'ist';
export type VatFilingPeriod = 'monthly' | 'quarterly' | 'yearly';
export type TaxReport = 'bwa' | 'balance' | 'euer' | 'vat_return';

export interface Mandant {
    id: number;
    club_id?: number;
    name: string;
    skr: Skr;
    datev_account_length?: number | null;
    legal_form?: LegalForm | null;
    is_nonprofit?: boolean;
    books_locked_until?: string | null;
    tax_number?: string | null;
    tax_office_number?: string | null;
    tax_office_name?: string | null;
    vat_id?: string | null;
    profit_determination?: ProfitDetermination | null;
    taxation_method?: TaxationMethod | null;
    small_business?: boolean;
    vat_filing_period?: VatFilingPeriod | null;
    permanent_extension?: boolean;
    fiscal_year_start_month?: number;
    accounting_start_date?: string | null;
    accounting_enabled?: boolean;
    accounting_locked?: boolean;
    last_closed_fiscal_year?: number | null;
    missing_tax_settings?: Record<TaxReport, string[]>;
    conversion_running?: boolean;
    skr_editable?: boolean;
}

export interface SetupStatus {
    needs_setup: boolean;
    mandanten_count: number;
    issuers_count: number;
}

export interface SkrRecommendation {
    skr: Skr;
    reason: string;
    alternatives: Skr[];
    labels: Record<Skr, string>;
}

export interface MandantTaxConflict {
    mandant_id: number;
    mandant_name: string;
    issuers: Array<{
        issuer_id: number;
        title: string;
        vat_number: string | null;
        vat_label: string | null;
    }>;
}

export type SkrConversionAction =
    | 'renumber_system'
    | 'renumber_mapped'
    | 'keep'
    | 'keep_review'
    | 'merge_into'
    | 'collision';

export type SkrConversionReason =
    | 'system_role'
    | 'chart_mapping'
    | 'already_target'
    | 'override'
    | 'custom_account'
    | 'unmapped_chart_account'
    | 'same_target_number'
    | 'occupied'
    | 'role_conflict';

export type SkrReportClass = 'balance' | 'pl';

export interface SkrConversionAccount {
    account_id: number;
    old_number: string;
    new_number: string;
    old_name: string;
    new_name: string;
    old_type: string | null;
    new_type: string | null;
    system_role: string | null;
    line_count: number;
    action: SkrConversionAction;
    reason: SkrConversionReason;
    merge_into_id: number | null;
    collides_with: number[];
    override: boolean;
    archived?: boolean;
    class_change?: { from: SkrReportClass | null; to: SkrReportClass | null } | null;
}

export interface SkrConversionReference {
    key: string;
    table: string;
    column: string;
    kind: 'column' | 'json';
    scope: 'mandant' | 'issuer_path' | 'club_wide' | 'club_setting';
    label: string;
    rows: number;
    included: boolean;
}

export interface SkrConversionNotice {
    code: string;
    message: string;
    account_id?: number;
    meta?: Record<string, any>;
}

export interface SkrConversionPlan {
    mandant_id: number;
    from_skr: Skr;
    to_skr: Skr;
    include_club_wide: boolean;
    include_club_wide_default: boolean;
    plan_hash: string;
    counts: Record<SkrConversionAction, number>;
    accounts: SkrConversionAccount[];
    number_map: Record<string, string>;
    references: SkrConversionReference[];
    warnings: SkrConversionNotice[];
    blocking: SkrConversionNotice[];
    overrides: Record<string, string>;
}

export interface SkrConversionSummary {
    accounts_renumbered: number;
    accounts_merged: number;
    accounts_flagged: number;
    lines_moved: number;
    lines_synced: number;
    references: Record<string, number>;
    references_total: number;
    include_club_wide: boolean;
}

export interface SkrConversionRun {
    id: number;
    mandant_id: number;
    from_skr: Skr;
    to_skr: Skr;
    status: 'queued' | 'running' | 'done' | 'failed';
    is_running: boolean;
    plan_hash: string;
    plan?: SkrConversionPlan;
    summary: SkrConversionSummary | null;
    error: string | null;
    user?: { id: number; name: string } | null;
    started_at: string | null;
    finished_at: string | null;
    created_at: string | null;
}

export const REQUIRED_SYSTEM_ROLES = [
    'receivable',
    'payable',
    'vat_out',
    'vat_in',
    'bank',
    'cash',
    'revenue',
    'expense'
] as const;

export const OPTIONAL_SYSTEM_ROLES = [
    'vat_out_7',
    'vat_in_7',
    'revenue_7',
    'revenue_exempt',
    'revenue_small_business',
    'opening_balance',
    'opening_balance_debtors',
    'opening_balance_creditors'
] as const;

export const SYSTEM_ROLES = [...REQUIRED_SYSTEM_ROLES, ...OPTIONAL_SYSTEM_ROLES] as const;
export type SystemRole = (typeof SYSTEM_ROLES)[number];

/** The account that already carries a role's chart number, so the default cannot be created. */
export interface SystemRolePresetHolder {
    id: number;
    number: string;
    name: string;
}

export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

export interface UserInvoiceIssuer extends ModelWithSoftDelete {
    title: string;
    description: null | string;
    scopes: Array<'invoicetool'>;
    mandant_id?: number;
}

export interface PaymentTerm extends ModelWithSoftDelete {
    title: string;
    key: string | null;
    remarks: string | null;
    payment_deadline_in_days: number | null;
}

export interface CostCenter extends ModelWithSoftDelete {
    short_description: string;
    description: string | null;
    number: string;
}

export type AccountReviewReason = 'skr_conversion' | 'orphan_lines' | 'duplicate_number' | 'adopted_by_number';

export interface Account extends ModelWithSoftDelete {
    name: string;
    description: string | null;
    number: string;
    type: string | null;
    system_role: string | null;
    mandant_id?: number | null;
    needs_review?: boolean;
    review_reason?: AccountReviewReason | null;
    merged_into_id?: number | null;
    default_tax_code?: TaxCode | null;
    sphere?: Sphere | null;
    bwa_position?: string | null;
    balance_position?: string | null;
    euer_position?: string | null;
}

export interface ReportPosition {
    key: string;
    label: string;
    is_sum: boolean;
    assignable: boolean;
}

export interface ReportPositions {
    skr: string | null;
    bwa: ReportPosition[];
    balance: (ReportPosition & { side: 'aktiva' | 'passiva' })[];
    euer: (ReportPosition & { line?: number | string | null })[];
}

/** Slim account row from `account/all`, for dropdowns and lookups. */
export type AccountOption = Pick<
    Account,
    'id' | 'mandant_id' | 'number' | 'name' | 'type' | 'system_role' | 'needs_review'
>;

export interface JournalLine {
    id: number;
    account_id: number | null;
    account_number: string;
    account_name?: string | null;
    account_deleted?: boolean;
    side: 'debit' | 'credit';
    amount_cents: number;
    currency: string;
    tax_code?: TaxCode | null;
    tax_rate?: number | string | null;
    sphere?: Sphere | null;
}

export interface JournalEntry {
    id: number;
    club_id?: number;
    mandant_id: number | null;
    description: string;
    booked_at: string;
    source_type: string;
    source_id: number;
    source_label?: string;
    reversal_of_id: number | null;
    is_reversed?: boolean;
    can_reverse?: boolean;
    /** Only on show, store and reverse, and only for manual bookings. */
    manual_booking?: ManualBooking | null;
    lines: JournalLine[];
    created_at?: string | null;
}

export type ManualBookingKind = 'manual' | 'opening_balance' | 'carry_forward';

export interface ManualBooking {
    id: number;
    kind: ManualBookingKind;
    fiscal_year: number | null;
    document_no: string | null;
    document_date: string | null;
    file_id: number | null;
    file_name: string | null;
    /** Signed, valid for 15 minutes. */
    file_url: string | null;
    bank_account_id: number | null;
    bank_transaction_provider_id: string | null;
    bank_amount: number | null;
    bank_booking_date: string | null;
    bank_counterparty: string | null;
    cancelled_at: string | null;
    created_by: { id: number; name: string } | null;
}

export interface InvoicePriceData {
    total_of_credits: string | null;
    total_of_taxes: string | null;
    total_without_taxes: string | null;
    total: string | null;
}

export type SepaReportRunStatus = 'created' | 'preparing' | 'success' | 'failed' | 'expired';

export type SepaReportRunSource = 'create' | 'update' | 'download';

export interface SepaReportFileInvoice extends Model {
    user_invoice_id: number | null;
    number: string | null;
    name: string | null;
    amount_cents: number;
    sequence_type: string | null;
    mandate_reference: string | null;
    iban_masked: string | null;
    rate_index: number | null;
}

export interface SepaReportFile extends Model {
    filename: string;
    direction: 'debit' | 'credit';
    chunk_index: number;
    issuer_id: number | null;
    issuer_title: string | null;
    account_owner: string | null;
    creditor_iban_masked: string | null;
    creditor_identifier: string | null;
    message_id: string | null;
    pain_format: string | null;
    transaction_count: number;
    amount_cents: number;
    sequence_types: Record<string, { count: number; amount_cents: number }> | null;
}

/** One generation of a report's XML files. Every download produces a new one. */
export interface SepaReportRun extends Model {
    title: string | null;
    requested_collection_date: string | null;
    batch_booking: boolean;
    mark_payed: boolean;
    include_summary: boolean;
    chunk_size: number;
    invoice_count: number;
    prenotification_count: number;
    /** Queued chunks. One chunk writes a debit file, a credit file, both or neither. */
    expected_chunks: number;
    /** Chunks that ran to the end. Only this compares meaningfully to expected_chunks. */
    chunks_done?: number;
    source: SepaReportRunSource | null;
    status: SepaReportRunStatus;
    file_download_id: number | null;
    user_id: number | null;
    files?: SepaReportFile[];
    files_count?: number;
    transaction_count?: number;
    amount_cents?: number;
}

export interface SepaReport extends Model {
    title: string | null;
    remarks: string | null;
    requested_collection_date: string | null;
    batch_booking: boolean;
    last_download: string | null;
    invoices_count?: number;
    runs_count?: number;
    files_count?: number;
    last_run?: SepaReportRun | null;
    runs?: SepaReportRun[];
}

export type UserInvoicePaymentMethod = 'SEPA' | 'CASH' | 'TRANSFER' | 'OTHER';

export interface UserInvoicePayment extends Model {
    remarks: string | null;
    payed_at: string | null;
    amount: number;
    method: UserInvoicePaymentMethod;
    user_invoice_id: UserInvoice['id'];
}

export interface UserInvoiceDunning extends Model {
    description: string;
    value: number;
    taxes: number | null;
    level: number;
    url?: string;
}

export interface UserInvoice extends ModelWithSoftDelete {
    title: string;
    number: string;
    remarks: string | null;
    header_title: string | null;
    header_entry: string | null;
    currency: CurrencyCode;

    items?: { description: string; description_long?: string | null; id: number }[];

    dunning_level: number | null;
    dunning_invoice_id: number | null;

    line0: string;
    line1: string | null;
    line2: string | null;
    city: string;
    postcode: string;
    country: string | null;
    email: string | null;

    payments_sum_amount: number;
    price_data: InvoicePriceData;
    reminder_for_id: number | null;
    payment_term_id: number | null;
    sepa_report_id: number | null;
    rates?:
        | {
              total: number;
              date: string;
              due_date: string | null;
              payed_at: string | null;
              sepa_report_id: number | null;
              sepa_downloaded_at: string | null;
          }[]
        | null;

    purpose: string | null;
    iban: string | null;
    bic: string | null;
    debtor: string | null;

    form_data_id: number | null;
    category_id: number | null;
    category_title: string | null;

    date: string;
    due_date: string;
    send_at: string | null;
    cancelled_at: string | null;
    cancellation_reason: string | null;
    payed_at: string | null;
    last_datev_export: string | null;
    sepa_downloaded_at: string | null;
    taxes: number;
    gross_value: boolean;

    is_paid: boolean;
    isClubMember: boolean | null;
    isCredit: boolean;
    isCorrect: boolean;
    finalized: boolean;

    // True once a ledger entry exists, false when the invoice is finalized but not booked
    // (e.g. the issuer has no Mandant). Only the invoice list resolves it.
    ledger_posted?: boolean;

    // True when Bankabgleich has an unresolved match suggestion for this invoice.
    has_match_suggestion?: boolean;
    match_suggestions?: import('./MatchSuggestionTooltip').InvoiceMatchSuggestion[];

    dunnings?: Pick<UserInvoice, 'id' | 'price_data' | 'dunning_level' | 'created_at' | 'send_at' | 'url'>[];

    credited_invoice_id?: number | null;
    credited_invoice?: { id: number; number: string; url: string } | null;
    credit_notes?: { id: number; number: string; url: string }[];

    setting_id: number;
    issuer: {
        id: number;
        title: string;
        mandant_id?: number;
    };

    total_cents?: number;
    bank_transactions?: {
        id: number;
        bank_account_id: number;
        bank_transaction_provider_id: string;
        amount: number | null;
        booking_date: string | null;
        counterparty: string | null;
        payment_id: number | null;
    }[] | null;

    // Linked online (Mollie) payment of the invoiceable booking, if any.
    online_payment?: { id: string; status: PaymentStatus } | null;

    url: string;
}

export interface UserInvoiceItem extends ModelWithSoftDelete {
    invoice_id: number;
    description: string;
    description_long?: string | null;
    amount: number;
    price_per_item: number;
    taxes: number | null;
}

export interface UserInvoiceCredit extends ModelWithSoftDelete {
    invoice_id: number;
    description: string;
    amount: number;
    percentage: boolean;
}

export interface UserInvoiceCategory extends Model {
    title: string;
    parent_category_id: number | null;
    all_sent?: boolean;
    all_settled?: boolean;
}

export interface UserInvoiceDelivery extends Model {
    subject: string;
    /** Invoice mailing or SEPA pre-notification run — both share this record. */
    type?: 'invoice' | 'sepa_prenotification';
    progress: number;
    invoices_count: number;
    failed_invoices: Partial<UserInvoice & { failed_at: string | null }>[];
}

export type SepaInterval = 'once' | 'monthly' | 'quarterly' | 'biannual' | 'annual';

/** Whether a collection still has to be announced to the payer. */
export type SepaAnnouncementStatus = 'required' | 'changed' | 'covered';

export interface SepaMandate extends Model {
    reference: string;
    sequence_type: string;
    account_holder: string | null;
    /** Masked — the list never needs the full IBAN. */
    iban: string | null;
    bic: string | null;
    signed_at: string | null;
    last_used_at: string | null;
    revoked_at: string | null;
    source: 'auto' | 'form' | 'manual';
    issuer_id: number | null;
    issuer?: string | null;
    payer_type: string | null;
    payer_id: number | null;
    has_pending_amendment: boolean;
    proof_file_id?: number | null;
    form_data_id?: number | null;
}

/** One row of the pre-notification preview. */
export interface SepaPrenotificationPreview {
    id: number;
    user_invoice_id: number;
    sepa_report_id: number | null;
    number: string | null;
    line0: string | null;
    email: string | null;
    /** Integer cents. */
    amount: number;
    collection_date: string;
    interval: SepaInterval;
    creditor_identifier: string | null;
    mandate_reference: string | null;
    iban: string | null;
    /** True when the invoice had no IBAN and the mandate supplied it. */
    iban_from_mandate: boolean;
    collectable: boolean;
    status: SepaAnnouncementStatus;
    previous_amount: number | null;
    previous_collection_day: number | null;
}

export interface UserInvoiceSetting extends Model {
    line0: string;
    line1: string | null;
    line2: string | null;
    city: string;
    postcode: string;
    country: string | null;

    currency: CurrencyCode;
    gross_value: boolean;
    /** Snapshot of the Mandant's tax id when this version was saved; maintained on the Mandant. */
    readonly vat_number: string | null;
    readonly vat_label: string | null;
    account_owner: string | null;
    iban: string | null;
    qr_iban: string | null;
    bic: string | null;
    creditor_identifier: string | null;
    email: string | null;
    phone: string | null;

    invoice_template: string | null;

    number_pattern: string | null;
    number_pattern_starts_at: string | null;

    issuer_id: number;
    small_business?: boolean;
    default_tax_rate?: number;
}

export type ReportKind = 'bwa' | 'balance-sheet' | 'euer' | 'vat-return';
export type ReportSphere = Sphere | 'unassigned';

export interface ReportPeriod {
    from: string | null;
    to: string;
    label: string;
    kind: 'year' | 'month' | 'quarter' | 'until' | 'range';
}

export interface FiscalYearInfo {
    start: string;
    end: string;
    label: string;
}

export interface ReportWarning {
    code: string;
    message: string;
    meta: {
        accounts?: { account_id: number | null; number: string | null; name: string | null }[];
        count?: number;
        [key: string]: unknown;
    };
}

export interface ReportAccount {
    account_id: number | null;
    number: string | null;
    name: string | null;
    amount_cents: number;
    deleted?: boolean;
    moved_from?: string | null;
    month_cents?: number | null;
    prev_amount_cents?: number | null;
    part?: 'base' | 'tax';
}

interface ReportBase<Type extends string> {
    type: Type;
    mandant_id: number;
    skr: Skr | null;
    period: ReportPeriod;
    warnings: ReportWarning[];
    generated_at: string;
}

export interface BwaPosition {
    key: string;
    label: string;
    is_sum: boolean;
    children: string[] | null;
    direction: 'credit' | 'debit' | null;
    amount_cents: number;
    month_cents: number | null;
    prev_amount_cents: number | null;
    delta_cents: number | null;
    delta_percent: number | null;
    share_percent: number | null;
    accounts: ReportAccount[];
}

export interface BwaReport extends ReportBase<'bwa'> {
    fiscal_year: FiscalYearInfo;
    columns: { month: ReportPeriod | null; cumulative: ReportPeriod; previous: ReportPeriod | null };
    sphere: ReportSphere | null;
    result: {
        key: string;
        label: string;
        amount_cents: number;
        month_cents: number | null;
        prev_amount_cents: number | null;
    } | null;
    positions: BwaPosition[];
}

export interface BalanceSheetPosition {
    key: string;
    label: string;
    side: 'aktiva' | 'passiva';
    is_sum: boolean;
    children: string[] | null;
    computed: 'net_income' | null;
    carry_forward: boolean;
    amount_cents: number;
    accounts: ReportAccount[];
}

export interface BalanceSheetReport extends ReportBase<'balance_sheet'> {
    date: string;
    fiscal_year: FiscalYearInfo;
    cutoff: string | null;
    net_income_cents: number;
    carry_forward_cents: number;
    totals: { aktiva_cents: number; passiva_cents: number };
    difference_cents: number;
    positions: BalanceSheetPosition[];
}

export type EuerSphereAmounts = Record<ReportSphere, number>;

export interface EuerPosition {
    key: string;
    label: string;
    line: string | null;
    group: 'einnahmen' | 'ausgaben' | null;
    is_sum: boolean;
    children: string[] | null;
    amount_cents: number;
    spheres: EuerSphereAmounts | null;
    accounts: ReportAccount[];
}

export interface EuerReport extends ReportBase<'euer'> {
    fiscal_year: FiscalYearInfo;
    form_year: number | string | null;
    basis: 'cash';
    sphere: ReportSphere | null;
    totals: { einnahmen_cents: number; ausgaben_cents: number; result_cents: number };
    spheres: { key: ReportSphere; label: string }[] | null;
    sphere_summary: {
        wirtschaftlich: {
            einnahmen_cents: number;
            ausgaben_cents: number;
            result_cents: number;
            threshold_cents: number;
            exceeds_threshold: boolean;
            notice: string;
        };
    } | null;
    positions: EuerPosition[];
}

export interface VatReturnPosition {
    key: string;
    kz: string;
    label: string;
    kind: 'base' | 'tax';
    is_sum: boolean;
    base_cents: number | null;
    base_exact_cents: number | null;
    tax_cents: number | null;
    tax_booked_cents?: number | null;
    amount_cents: number;
    accounts: ReportAccount[];
}

export interface VatReturnReport extends ReportBase<'vat_return'> {
    taxation_method: TaxationMethod;
    vat_filing_period: VatFilingPeriod | null;
    permanent_extension: boolean;
    not_applicable?: boolean;
    deadline: string | null;
    totals: {
        output_tax_cents: number;
        output_tax_booked_cents?: number;
        rounding_difference_cents?: number;
        input_tax_cents: number;
        payable_cents: number;
    } | null;
    positions: VatReturnPosition[];
}

export interface ReportByKind {
    bwa: BwaReport;
    'balance-sheet': BalanceSheetReport;
    euer: EuerReport;
    'vat-return': VatReturnReport;
}

export interface ReportStatus {
    mandant_id: number;
    skr: Skr;
    missing_tax_settings: Record<TaxReport, string[]>;
    accounts_without_position: Record<'bwa' | 'balance' | 'euer', number>;
    accounts_without_position_ids: Record<'bwa' | 'balance' | 'euer', number[]>;
    lines_without_tax_code: number;
    opening_balance_difference_cents: Record<string, number>;
    double_counted_accounts: {
        account_id: number;
        number: string;
        name: string;
        bwa_position: string | null;
        balance_position: string | null;
    }[];
    accounts_without_type: number;
    accounts_without_type_ids: number[];
    accounts_off_chart: number;
    accounts_off_chart_ids: number[];
    accounts_off_chart_details: {
        account_id: number;
        number: string;
        name: string;
        expected_type: AccountType | null;
        actual_type: AccountType | null;
    }[];
    accounts_unclassified: number;
    accounts_unclassified_ids: number[];
    profit_determination: ProfitDetermination | null;
    taxation_method: TaxationMethod | null;
    vat_filing_period: VatFilingPeriod | null;
    permanent_extension: boolean;
    small_business: boolean;
    is_nonprofit: boolean;
    fiscal_year_start_month: number;
    accounting_enabled: boolean;
    accounting_start_date: string | null;
    entries_before_start: number;
    fiscal_years: (FiscalYearInfo & { year: number })[];
}
